/**
 * Helpers for turning a stored blob/SAS URL into a clean, user-facing file name
 * and a file-type badge (label + colour class), shared by the upload and history dialogs.
 */

export interface FileBadge {
  label: string;
  className: string;
}

/**
 * Extracts a readable file name from a URL/blob path, dropping any query string
 * (e.g. SAS token) and decoding percent-encoding.
 * "https://…/Edusecure Brochure 2024.pdf?sv=2025-05-05&sig=…" → "Edusecure Brochure 2024.pdf"
 */
export function getFileNameFromUrl(value: string): string {
  if (typeof value !== 'string' || !value) return '';
  const noQuery = value.split('?')[0];
  const last = noQuery.split('/').pop() || noQuery;
  try {
    return decodeURIComponent(last);
  } catch {
    return last;
  }
}

function getExtension(value: string): string {
  const name = getFileNameFromUrl(value);
  const dot = name.lastIndexOf('.');
  return dot >= 0 ? name.slice(dot + 1).toLowerCase() : '';
}

/** Maps a file name/URL to a coloured type badge (PDF, DOCX, JPG, …). */
export function getFileBadge(value: string): FileBadge {
  switch (getExtension(value)) {
    case 'pdf': return { label: 'PDF', className: 'file-badge--pdf' };
    case 'doc':
    case 'docx': return { label: 'DOCX', className: 'file-badge--doc' };
    case 'xls':
    case 'xlsx':
    case 'csv': return { label: 'XLS', className: 'file-badge--xls' };
    case 'ppt':
    case 'pptx': return { label: 'PPT', className: 'file-badge--ppt' };
    case 'jpg':
    case 'jpeg': return { label: 'JPG', className: 'file-badge--img' };
    case 'png': return { label: 'PNG', className: 'file-badge--img' };
    case 'gif': return { label: 'GIF', className: 'file-badge--img' };
    default: {
      const ext = getExtension(value);
      return { label: ext ? ext.toUpperCase().slice(0, 4) : 'FILE', className: 'file-badge--generic' };
    }
  }
}
